"use client"

import { useEffect, useState } from "react"
import {
  Bot,
  Users,
  BarChart3,
  Shield,
  Search,
  Tag,
  Send,
  GraduationCap,
  Megaphone,
  Building2,
  ChevronRight,
  Sparkles,
} from "lucide-react"

interface ChatWelcomeProps {
  onSuggestion: (text: string) => void
}

/**
 * Time-of-day greeting computed from the user's local clock.
 * Bands (24h, local tz):
 *   05:00–11:59 → "Good morning"
 *   12:00–16:59 → "Good afternoon"
 *   17:00–04:59 → "Good evening"
 */
function getLocalGreeting(now: Date = new Date()): string {
  const h = now.getHours()
  if (h >= 5 && h < 12) return "Good morning"
  if (h >= 12 && h < 17) return "Good afternoon"
  return "Good evening"
}

// Multi-module suggestion matrix (2026-05-26).
//
// Why these 10:
//   * 2 from each top module (Recipients, Phishing, LMS, Announcements)
//     + 2 cross-cutting (Data quality, Audit log) so the welcome screen
//     mirrors the real toolspace, not just one feature.
//   * Each `prompt` is the LITERAL text injected into the chat input,
//     so it has to be a self-contained question the LLM can answer
//     against the wired tool registry (recipients/phishing/lms/etc.).
//   * `module` label is shown as a tiny pill so the user sees at a
//     glance which area the question lives in.
type ModuleTag =
  | 'Recipients'
  | 'Phishing'
  | 'Training'
  | 'Announcements'
  | 'Insights'
  | 'Audit'

interface Suggestion {
  module: ModuleTag
  icon: React.ComponentType<{ className?: string }>
  title: string
  prompt: string
  accent: string  // text-color for icon
}

const suggestions: Suggestion[] = [
  // ── Recipients (2) ────────────────────────────────────────────────
  {
    module: 'Recipients',
    icon: Search,
    title: 'Active recipients',
    prompt: 'Show me all active recipients',
    accent: 'text-blue-500',
  },
  {
    module: 'Recipients',
    icon: Building2,
    title: 'By department',
    prompt: 'Group recipients by department and show counts',
    accent: 'text-rose-500',
  },

  // ── Phishing (2) ──────────────────────────────────────────────────
  {
    module: 'Phishing',
    icon: Send,
    title: 'Recent campaigns',
    prompt: 'List the last 10 phishing campaigns with their click rates',
    accent: 'text-fuchsia-500',
  },
  {
    module: 'Phishing',
    icon: Tag,
    title: 'Top scenarios',
    prompt: 'Which phishing scenarios have the highest compromise rates?',
    accent: 'text-orange-500',
  },

  // ── Training (2) ──────────────────────────────────────────────────
  {
    module: 'Training',
    icon: GraduationCap,
    title: 'Enrolment status',
    prompt: 'How many recipients are still pending training completion?',
    accent: 'text-emerald-500',
  },
  {
    module: 'Training',
    icon: Sparkles,
    title: 'Course catalog',
    prompt: 'Show all published training courses',
    accent: 'text-teal-500',
  },

  // ── Announcements (1) ─────────────────────────────────────────────
  {
    module: 'Announcements',
    icon: Megaphone,
    title: 'Active announcements',
    prompt: 'List active announcements with open rates',
    accent: 'text-amber-500',
  },

  // ── Cross-cutting (3) ─────────────────────────────────────────────
  {
    module: 'Insights',
    icon: BarChart3,
    title: 'Data quality',
    prompt: "What's our recipient data quality score?",
    accent: 'text-cyan-500',
  },
  {
    module: 'Insights',
    icon: Users,
    title: 'Find duplicates',
    prompt: 'Find duplicate recipients across all workspaces',
    accent: 'text-indigo-500',
  },
  {
    module: 'Audit',
    icon: Shield,
    title: 'Recent activity',
    prompt: 'Show me the last 20 admin audit log entries',
    accent: 'text-violet-500',
  },
]

export function ChatWelcome({ onSuggestion }: ChatWelcomeProps) {
  // SSR-safe: server renders the neutral "Hello" (any deterministic string
  // works); client effect swaps in the time-of-day greeting after mount so
  // hydration doesn't trip on the server-vs-client clock mismatch.
  const [greeting, setGreeting] = useState<string>("Hello")
  useEffect(() => {
    setGreeting(getLocalGreeting())
  }, [])

  return (
    <div
      className="@container flex flex-col items-center flex-1 overflow-y-auto px-5 pt-6 pb-4"
      style={{
        backgroundImage:
          "radial-gradient(circle, rgba(0,0,0,0.06) 1px, transparent 1px)",
        backgroundSize: '16px 16px',
      }}
    >
      <div className="flex items-center justify-center h-11 w-11 rounded-xl bg-primary/10 mb-3">
        <Bot className="h-5 w-5 text-primary" />
      </div>

      <h2 className="text-base font-semibold mb-0.5">{greeting}.</h2>
      <p className="text-xs text-muted-foreground mb-4 text-center">
        What are we doing today?
      </p>

      {/* Responsive matrix keyed on the CONTAINER width (not the viewport).
          The welcome wrapper above is `@container`, so these columns react to
          the CHAT panel's width, not the browser window. Critical because the
          same ChatWelcome renders both in the full /recipients/ai-chat page
          (wide → 2 cols) AND in the narrow side-drawer / companion (~400px →
          1 col). The old `md:grid-cols-2` used the viewport breakpoint, so a
          wide window forced 2 columns into the narrow drawer → squashed cards +
          truncated titles (2026-05-29). `@2xl` (≈672px container) keeps each
          card ≥~330px before going two-up. */}
      <div className="grid grid-cols-1 @2xl:grid-cols-2 gap-1.5 w-full max-w-4xl">
        {suggestions.map((s) => (
          <button
            key={`${s.module}-${s.title}`}
            onClick={() => onSuggestion(s.prompt)}
            className="group flex items-start gap-2.5 pl-2.5 pr-3 py-2 rounded-lg border border-border/60 bg-background/70 backdrop-blur-sm hover:bg-background hover:border-border hover:shadow-sm text-left transition-all"
          >
            <div
              className={`flex items-center justify-center h-7 w-7 rounded-md bg-muted/50 ${s.accent} shrink-0 mt-0.5`}
            >
              <s.icon className="h-3.5 w-3.5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground/70">
                  {s.module}
                </span>
                <span className="text-[13px] font-medium leading-tight text-foreground truncate">
                  {s.title}
                </span>
              </div>
              <div className="text-[11px] leading-snug text-muted-foreground line-clamp-2">
                {s.prompt}
              </div>
            </div>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/0 group-hover:text-muted-foreground transition-colors shrink-0 mt-1" />
          </button>
        ))}
      </div>

      <p className="text-[10px] text-muted-foreground/50 mt-3 text-center">
        Or type your own question below — the chat can reach{' '}
        <span className="font-medium text-muted-foreground/70">
          400+ tools across Recipients, Phishing, Training &amp; more
        </span>
        .
      </p>
    </div>
  )
}
